import { useState, useRef, useCallback, useEffect, useLayoutEffect, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { Mic, Paperclip, Volume2, Square, ArrowUp, Link, Settings, Keyboard, Check, Circle, X, Clock, Play, Pause, RotateCcw, Plus, Wand2 } from 'lucide-react';
import { api } from '../api/client';
import { ALL_MCP_SERVERS } from '../api/mcp';
import { useOutbox } from '../hooks/useOutbox';
import { notificationsEnabled, setNotificationsEnabled, requestNotificationPermission } from '../hooks/useDoneNotifications';
import { logToApi, type LogType } from '../lib/logApi';
import { pushToast, dismissToastByKey } from '../lib/toastStore';
import { getSseBus } from '../lib/sseBus';
import { dispatchDecision, mergeAttachUrls } from '../lib/attachmentGate';
import { useServiceAvailable } from '../hooks/useServiceOrigin';
import { serviceOrigin } from '../lib/services';
import type { V2Section, V2Turn } from '../api/types';
import './InputBar.css';

// ── TTS speakable-text extraction ────────────────────────────────────
// Shared by both TTS paths (native + OpenAI). Two helpers:
//   sectionsToSpeakable(sections) — strip tool-progress echoes, unwrap
//     kind='json' content, return a single concatenated string.
//   collectSpeakableSinceLastUserTurn(turns) — walk turns to find the
//     last direct/queue turn (= operator's last own input), then sum
//     up the agent reply for that turn PLUS every later turn's
//     incoming text (from iacp/queue/system, sender-prefixed) + agent
//     reply. Goal: when the operator hits TTS after agents have been
//     chatting among themselves, they hear the whole thread since
//     their last own message, not just the very last reply.

function sectionsToSpeakable(sections: V2Section[] | undefined): string {
  return (sections || [])
    .map((s) => {
      if (s.kind === 'text' && s.content) return s.content;
      if (s.kind === 'json' && s.content) {
        try {
          const parsed = JSON.parse(s.content);
          if (parsed && typeof (parsed as { content?: unknown }).content === 'string') {
            return (parsed as { content: string }).content;
          }
        } catch { /* skip */ }
      }
      return '';
    })
    .filter(Boolean)
    // Drop tool-progress echoes — claude-code renders them as
    // kind:'text' sections but they're transient status lines
    // ("Searching for …", "Reading file …", …). Reading them to the
    // user is never the reply they actually want. Cloud's IACP
    // 3be44e7a.
    .filter((c) => {
      const head = c.trim().slice(0, 60);
      if (/^(Searching for|Reading (file|files)|Running|Looking at|Listing|Fetching|Calling) /i.test(head)) return false;
      if (/^⎿/.test(head)) return false;
      return true;
    })
    .join(' ');
}

function stripIacpEnvelope(text: string): string {
  return text
    .replace(/^\[IACP:[^\]]*:[^\]]*\]\s*/, '')
    .replace(/^\[Queue Message #\d+ from [^\]]+\]:\s*/, '')
    .replace(/\s*\[IACP-PROTOCOL:[^\]]*\]\s*$/, '')
    .trim();
}

interface SpeakBundle {
  text: string;
  turnCount: number;       // how many turns were included
  sinceUserTurn: boolean;  // true when we found a direct/queue boundary
}

function collectSpeakableSinceLastUserTurn(turns: V2Turn[]): SpeakBundle {
  if (!turns.length) return { text: '', turnCount: 0, sinceUserTurn: false };

  // Find boundary: most-recent direct or queue turn = operator's own
  // input. Everything after that is what they may have missed.
  let cutIdx = -1;
  for (let i = turns.length - 1; i >= 0; i--) {
    if (turns[i].source === 'direct' || turns[i].source === 'queue') {
      cutIdx = i;
      break;
    }
  }
  const sinceUserTurn = cutIdx >= 0;
  if (cutIdx < 0) cutIdx = 0;

  const chunks: string[] = [];
  for (let i = cutIdx; i < turns.length; i++) {
    const turn = turns[i];
    // For the boundary turn itself (= the operator's own message),
    // skip turn.user — they wrote it, no need to read it back. For
    // every subsequent turn (incoming iacp/queue/system) include the
    // incoming text with a sender tag so the summary keeps context.
    if (i !== cutIdx && turn.user) {
      const cleaned = stripIacpEnvelope(turn.user);
      if (cleaned) {
        const senderTag = turn.sender
          ? `${turn.sender}: `
          : (turn.source === 'iacp' ? 'Ein Agent: '
            : turn.source === 'queue' ? 'User: '
            : turn.source === 'system' ? 'System: '
            : '');
        chunks.push(senderTag + cleaned);
      }
    }
    const reply = sectionsToSpeakable(turn.sections);
    if (reply) chunks.push(reply);
  }
  return {
    text: chunks.join('\n\n'),
    turnCount: turns.length - cutIdx,
    sinceUserTurn,
  };
}

interface Props {
  sessionName: string;
  onSending?: (sending: boolean) => void;
  agentBusy?: boolean;
  /** Open the group-question dialog with THIS agent pre-selected (the "+"
   *  in the input row, Alex 2026-06-20). */
  onAskGroup?: () => void;
  /** Custom send target — when set, the message text routes here instead of
   *  the agent outbox/sendInput path. Lets the same composer (attachments,
   *  mic, send) drive a collab reply (Alex 2026-06-20). */
  onSubmit?: (text: string) => void | Promise<void>;
  /** Override the textarea placeholder ("Antworten…" in collab). */
  placeholder?: string;
  /** External gate — when explicitly false, send is blocked (text kept).
   *  Used by collab-compose to require recipients before the first send. */
  canSubmit?: boolean;
  /** Hide the global TTS speaker (group chat reads per-bubble instead — the
   *  composer's "read everything" mode makes no sense there, Alex 2026-06-20). */
  hideTts?: boolean;
  /** Put the media-attach (paperclip) on the LEFT instead of after the
   *  textarea — used in the agent chat where the "+" moved to the header
   *  (Alex 2026-06-22). */
  attachLeft?: boolean;
  /** Short name to append to outgoing messages as an author hint — set ONLY
   *  when this agent isn't the sender's own (Alex 2026-07-30: "wenn ich als
   *  admin in den Agenten Adam reingeh und sende, könnte hinten von Alex
   *  dranhängen"). Deliberately NOT set for one's own agents: they'd get the
   *  same tag on every single message for no information gain. Empty/undefined
   *  = append nothing. */
}

/** Imperative handle exposed to parent so drag-drop in AgentDetail can
 *  funnel files into the same attachments list as the paperclip button —
 *  yielding identical thumbnail-with-X chips above the input. Avoids the
 *  earlier "drop ⇒ url-text-pasted-into-textarea" behaviour Alex hit. */
export interface InputBarHandle {
  addFiles(files: File[] | FileList): Promise<void>;
}

type MicState = 'idle' | 'recording' | 'transcribing' | 'review' | 'error';

// VU-meter geometry: number of vertical bars rendered in the
// recording-bar. Each bar shows the amplitude of one frequency band so
// the meter dances vertically (equalizer-style) instead of lighting up
// left-to-right by threshold.
const VU_BAR_COUNT = 12;
const MAX_CHAT_UPLOAD_BYTES = 50 * 1024 * 1024;

const KEY_BUTTONS = [
  { label: 'Esc', key: 'Escape', cls: '' },
  { label: '\u232B', key: 'Backspace', cls: '' },
  { label: '\u2191', key: 'Up', cls: '' },
  { label: '\u2193', key: 'Down', cls: '' },
  { label: 'Enter', key: 'Enter', cls: 'key-success' },
  { label: 'Tab', key: 'Tab', cls: '' },
  { label: '^C', key: 'C-c', cls: 'key-error' },
  { label: '^B', key: 'C-b', cls: 'key-purple' },
  { label: '^O', key: 'C-o', cls: 'key-warning' },
  { label: '/resume', key: '/resume', cls: 'key-accent' },
] as const;

function loadDraft(name: string): string {
  try {
    const drafts = JSON.parse(localStorage.getItem('cloud-session-drafts') || '{}');
    return drafts[name] || '';
  } catch { return ''; }
}

function saveDraft(name: string, text: string) {
  try {
    const drafts = JSON.parse(localStorage.getItem('cloud-session-drafts') || '{}');
    if (text) drafts[name] = text;
    else delete drafts[name];
    localStorage.setItem('cloud-session-drafts', JSON.stringify(drafts));
  } catch { /* ignore */ }
}

// Outbox logic lives in the shared useOutbox hook (hooks/useOutbox.ts)
// — same storage key + semantics, now also consumed by MultiAgent so
// every send-surface gets the persist-before-POST guarantee.

// Native (Siri/Web-Speech) TTS persistence. On iOS the OS speech engine
// keeps talking across an F5 reload, but the page's `isSpeaking` state
// is gone so the Stop button vanishes — the user is stuck listening.
// We persist a marker with an estimated end-time; on mount we restore
// the speaking-state (and thus the Stop button) for as long as the
// utterance is plausibly still playing, so `speechSynthesis.cancel()`
// can be fired from the fresh page context.
const NATIVE_TTS_KEY = 'cloud-tts-native-active';
interface NativeTtsMarker { sessionName: string; preview: string; expiresAt: number; }
function setNativeTtsMarker(m: NativeTtsMarker) {
  try { localStorage.setItem(NATIVE_TTS_KEY, JSON.stringify(m)); } catch { /* quota */ }
}
function clearNativeTtsMarker() {
  try { localStorage.removeItem(NATIVE_TTS_KEY); } catch { /* no-op */ }
}
function readNativeTtsMarker(): NativeTtsMarker | null {
  try {
    const raw = localStorage.getItem(NATIVE_TTS_KEY);
    if (!raw) return null;
    const m = JSON.parse(raw) as NativeTtsMarker;
    if (!m.expiresAt || m.expiresAt < Date.now()) { clearNativeTtsMarker(); return null; }
    return m;
  } catch { return null; }
}

export const InputBar = forwardRef<InputBarHandle, Props>(function InputBar(
  { sessionName, onSending, agentBusy, onAskGroup, onSubmit, placeholder, canSubmit, hideTts, attachLeft }: Props,
  ref
) {
  // Diktat läuft über cloud-api /api/transcribe → api-ai. Deklariert die
  // Instanz die Sprachschicht mit null, kann das nie gelingen → Knopf weg (77eb0c9).
  const aiAvailable = useServiceAvailable('ai');
  const storageAvailable = useServiceAvailable('storage');
  const [input, setInput] = useState(() => loadDraft(sessionName));
  const [showKeys, setShowKeys] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showSendMenu, setShowSendMenu] = useState(false);
  const [showMcp, setShowMcp] = useState(false);
  const [mcpServers, setMcpServers] = useState<Record<string, boolean>>({});
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  // Senden hat hochgeladene Anhänge als VORAUSSETZUNG (Alex 2026-08-04: "dass es
  // nicht sendet ohne das Bild, sondern der Send als Requirement hat, dass die
  // hinzugefügten Bilder auch mitgeliefert werden").
  //
  // Vorher ein stiller Datenverlust: `processFiles` hängt eine Datei erst NACH
  // abgeschlossenem Upload an `attachments`, und kein Sendeweg hat `uploading`
  // geprüft. Wer während des Uploads sendete, verschickte den Text ohne Bild —
  // ohne jeden Hinweis, und der Anhang war weg, weil das Feld danach geleert
  // wird.
  //
  // Statt den Knopf hart zu sperren (Klick tut nichts, wirkt wie ein Bug) wird
  // die ABSICHT gemerkt und ausgeführt, sobald die Uploads durch sind. Das ist
  // die Bedeutung von "warte kurz" ohne dass der Nutzer warten muss.
  const [pendingSend, setPendingSend] = useState<
    'send' | 'queue' | 'promptAgent' | 'promptAgentFront' | 'reviewSend' | 'reviewQueue' | 'reviewPromptAgent' | null
  >(null);
  const [ttsStatus, setTtsStatus] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  // Which engine is currently producing audio — drives the playback
  // control bar (native/Siri gets only Stop; OpenAI-wave gets
  // pause/resume + 15s rewind because it's a seekable HTMLAudioElement).
  const [ttsKind, setTtsKind] = useState<'native' | 'openai' | null>(null);
  const [ttsPaused, setTtsPaused] = useState(false);
  const ttsAudioRef = useRef<HTMLAudioElement | null>(null);
  const [autoTts, setAutoTts] = useState(() => localStorage.getItem('cloud-auto-tts') === '1');
  // Browser-notification toggle — when on, you get an OS notification +
  // tab-title badge when the agent finishes while the tab is hidden.
  const [notifyDone, setNotifyDone] = useState(() => notificationsEnabled());
  // TTS engine + style. Three options:
  //   'native'         — Web Speech / Siri, free + instant, reads as-is
  //   'openai-compact' — server-side /api/tts-text style=compact;
  //                      2-3 Sätze, Big Picture, driving-mode (default openai)
  //   'openai-prose'   — server-side /api/tts-text style=prose;
  //                      150-300 Wörter narrative, mehr Detail behalten
  // Toggle via the TTS button's long-press menu. Persisted per-browser.
  // Legacy localStorage value 'openai' maps to 'openai-compact'.
  const [ttsMode, setTtsMode] = useState<'native' | 'openai-compact' | 'openai-prose'>(() => {
    const raw = localStorage.getItem('cloud-tts-mode');
    if (raw === 'openai-prose') return 'openai-prose';
    if (raw === 'openai-compact' || raw === 'openai') return 'openai-compact';
    return 'native';
  });

  // Live sync with Settings panel — Settings dispatches a custom event
  // when the user picks a new default. The `storage`-event would only
  // fire across tabs, not within the same tab, so we use a custom event
  // for in-tab live updates.
  useEffect(() => {
    const onModeChange = (e: Event) => {
      const ce = e as CustomEvent<string>;
      const v = ce.detail;
      if (v === 'native' || v === 'openai-compact' || v === 'openai-prose') {
        setTtsMode(v);
      }
    };
    window.addEventListener('cloud-tts-mode-change', onModeChange as EventListener);
    return () => window.removeEventListener('cloud-tts-mode-change', onModeChange as EventListener);
  }, []);
  const [attachments, setAttachments] = useState<Array<{ url: string; preview?: string; name: string }>>([]);
  const [micState, setMicState] = useState<MicState>('idle');
  const [micTime, setMicTime] = useState(0);
  const [reviewText, setReviewText] = useState('');
  // Tap-feedback for the green Send-Voice button — flips true the moment
  // it's pressed so the UI confirms the tap landed before the async
  // stop/transcribe pipeline runs (Alex: "ich weiß nicht ob ich geklickt
  // hab"). Cleared when recording state leaves.
  const [voiceSendPressed, setVoiceSendPressed] = useState(false);
  // Which attachment is shown large in the recording-state gallery —
  // swipeable so the user can look at the images they're about to
  // comment on while speaking.
  const [galleryIndex, setGalleryIndex] = useState(0);
  const galleryTouchX = useRef<number | null>(null);
  // Brief "sent!" confirmation flash on the primary Send button. The
  // input clearing is subtle; users (Alex 2026-06-15) couldn't tell the
  // message actually went to the agent. Flips true on send, shows a
  // green check for ~700 ms, then back to the arrow.
  const [sentFlash, setSentFlash] = useState(false);
  const sentFlashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerSentFlash = useCallback(() => {
    setSentFlash(true);
    if (sentFlashTimer.current) clearTimeout(sentFlashTimer.current);
    sentFlashTimer.current = setTimeout(() => setSentFlash(false), 700);
    if (navigator.vibrate) navigator.vibrate(20);
  }, []);

  const sendingRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const ttsLongPressRef = useRef(false);
  const ttsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Long-press on the send arrow defers the message into a local queue
  // and auto-flushes when the agent transitions to idle. Lets Alex type
  // ahead while the agent is still working without immediately
  // interrupting it.
  const sendLongPressRef = useRef(false);
  const sendTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Queued messages keyed BY sessionName. A long-press queue must stay
  // bound to the agent it was written for — the previous `{ text }` had
  // no agent binding, so switching sessions kept the same component state
  // and the auto-flush fired the queued text into whatever agent was now
  // open (Alex 2026-06-19: "pushed into the wrong agent"). Per-session
  // keying also stops two agents' queued texts getting concatenated.
  // Warteschlange je Agent als LISTE, nicht als ein verketteter Text.
  //
  // Alex, 12.08.: „Ich hab da jetzt 3 Msgs in die Queue gegeben, aber keins
  // davon sehe [ich] — es sieht so aus, als gebe es in der Queue nur eins."
  // Verloren war nichts: Der alte Code hängte mit `\n\n` an, die Leiste zeigte
  // aber nur die ersten 80 Zeichen. Drei Einträge sahen aus wie einer.
  //
  // Als Liste ist die Anzahl eine Tatsache statt einer Schätzung (ein Zählen
  // über Leerzeilen wäre falsch, sobald eine Nachricht selbst welche enthält),
  // und einzelne Einträge lassen sich entfernen. Gesendet wird weiterhin EIN
  // Block: Drei Gedanken zum selben Thema will man nicht in drei Zügen
  // beantwortet bekommen.
  const [queuedMessages, setQueuedMessages] = useState<Record<string, string[]>>({});
  const queuedList = queuedMessages[sessionName] ?? null;
  const queuedText = queuedList?.length ? queuedList.join('\n\n') : null;
  // Hide the send arrow when there's nothing to send; the mic then becomes
  // the prominent action (Alex 2026-06-22).
  const showSend = input.trim().length > 0 || !!queuedText || sentFlash;
  const clearQueuedForSession = useCallback(() => {
    setQueuedViaPa((prev) => {
      if (!(sessionName in prev)) return prev;
      const { [sessionName]: _gone, ...rest } = prev; void _gone; return rest;
    });
    setQueuedMessages((prev) => {
      if (!(sessionName in prev)) return prev;
      const { [sessionName]: _gone, ...rest } = prev;
      void _gone;
      return rest;
    });
  }, [sessionName]);

  // Persisted retry outbox — shared hook with MultiAgent (useOutbox).
  // Survives reload, offline, server 5xx; auto-retries every 8 s + on
  // the browser `online` event. In-flight guard in the hook prevents
  // double-send when multiple surfaces are mounted for one session.
  const { sessionOutbox, isOnline, enqueueAndSend, flushItem, discardSession } = useOutbox(sessionName);

  // Single send funnel — every path (typed, voice, queued) goes through
  // here. Default = the agent outbox (persist-before-POST + retry). When
  // `onSubmit` is provided (collab), route there instead.
  const submit = useCallback((text: string) => {
    if (onSubmit) { void Promise.resolve(onSubmit(text)); }
    else {
      // ENTFERNT am 18.08. (Alex: „warum ist das noch so drinnen … das ist
      // falsch"). Bis gestern hängte das Portal bei fremden Agenten „— von
      // <Vorname>" an den TEXT, weil der Server den Absender nicht festhielt
      // (Regel vom 30.07.). Seit der Attributions-Kette (#4701 Phase 1) hält
      // `input_dedup` den AUTHENTIFIZIERTEN Absender fest und `/history`
      // liefert ihn als `submitted_by` — belegt statt geraten, und ohne die
      // Nachricht des Nutzers zu verändern.
      //
      // Zwei Gründe, warum der Anhang jetzt schädlich ist: (1) Er verfälscht
      // die Anweisung, die der Agent liest (der Vorname wird Teil des
      // Auftrags). (2) Auf einer KUNDENINSTANZ trüge der durable Verlauf des
      // Kunden den Vornamen des Betreibers — Identität, die dort nicht
      // hingehört.
      enqueueAndSend(text);
    }
  }, [onSubmit, enqueueAndSend]);
  const mediaRecRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  // Set true when the user cancels (X) — checked in rec.onstop so the
  // final flushed `dataavailable` chunk doesn't get transcribed + sent
  // anyway. Without it, cancel still ran the whole transcribe pipeline
  // (Alex's "schreibt trotzdem Transkribiere" bug).
  const cancelledRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Web-Audio VU-meter: AnalyserNode reads the live mic stream so the
  // recording bar shows real-time audio level. Confirms to the user
  // that their voice is actually arriving instead of staring at a
  // static red dot wondering whether the mic is dead. Cleaned up in
  // stopRecording alongside the MediaRecorder + MediaStream.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  // Per-band frequency amplitudes (0-1) for the VU equalizer — one value
  // per visible bar. Length == VU_BAR_COUNT.
  const [audioBands, setAudioBands] = useState<number[]>(() => new Array(VU_BAR_COUNT).fill(0));
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load draft when session changes
  useEffect(() => {
    setInput(loadDraft(sessionName));
  }, [sessionName]);

  // Recap "Next:"-chip prefill — RecapCard dispatches a window event so
  // we don't thread a callback through the turn-render tree. Append to
  // any existing draft (with a separator) rather than clobbering it, and
  // focus the textarea so the user can edit before sending.
  useEffect(() => {
    const onPrefill = (e: Event) => {
      const ce = e as CustomEvent<{ text?: string }>;
      const text = ce.detail?.text?.trim();
      if (!text) return;
      setInput((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
      requestAnimationFrame(() => textareaRef.current?.focus());
    };
    window.addEventListener('cloud-prefill-input', onPrefill as EventListener);
    return () => window.removeEventListener('cloud-prefill-input', onPrefill as EventListener);
  }, []);

  // Restore native-TTS speaking state after an F5 reload. On iOS the OS
  // speech engine keeps talking through a reload but the page state is
  // fresh — without this the Stop button is gone and the user can't
  // silence Siri. We re-show the speaking state until the estimated end
  // (or the user hits Stop), and auto-clear at the marker's expiry.
  useEffect(() => {
    const marker = readNativeTtsMarker();
    if (!marker) return;
    setIsSpeaking(true);
    setTtsKind('native');
    setTtsStatus(`🔊 ${marker.preview} (läuft noch — Stop drücken)`);
    const remaining = marker.expiresAt - Date.now();
    const t = setTimeout(() => {
      setIsSpeaking(false);
      setTtsKind(null);
      setTtsStatus('');
      clearNativeTtsMarker();
    }, Math.max(0, remaining));
    return () => clearTimeout(t);
    // Mount-only — restoring once on load is the whole point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save draft on change — DEBOUNCED. saveDraft parses + re-stringifies the
  // whole drafts blob (every session's draft), so doing it per keystroke is
  // real O(n) work on each character and showed up as typing lag on a big
  // draft set (Alex, 2026-07-01). Persist ~400ms after typing pauses instead;
  // a draft is a convenience, it doesn't need per-character durability.
  useEffect(() => {
    const t = setTimeout(() => saveDraft(sessionName, input), 400);
    return () => clearTimeout(t);
  }, [input, sessionName]);

  // Hide the voice-route toggle whenever recording ends (idle/error) — covers the
  // cancel / empty-blob / error exit paths. The ref is re-set at each record-start.
  useEffect(() => {
    if (micState === 'idle' || micState === 'error') setVoiceRouteActive(false);
  }, [micState]);

  // Auto-TTS: trigger when agent goes from busy to idle
  const prevBusyRef = useRef(agentBusy);
  useEffect(() => {
    if (autoTts && prevBusyRef.current && !agentBusy) {
      // Agent just finished — trigger TTS after short delay
      setTimeout(() => handleTTS(), 1500);
    }
    prevBusyRef.current = agentBusy;
  }, [agentBusy, autoTts]);

  // (Old appendText prop was a workaround that pasted upload URLs into the
  // textarea. Replaced by the addFiles imperative handle below so drag-drop
  // produces the same thumbnail chips as the paperclip button.)

  const resetTextarea = useCallback(() => {
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }, []);

  // Auto-grow the textarea to fit its content — but OUT of the keystroke path.
  // Reading scrollHeight forces a synchronous layout; doing that inline in
  // onChange means a full reflow on every character, and with a long, SSE-
  // churned transcript above, the layout is perpetually dirty → each read is a
  // full relayout → extreme typing lag (Alex, 2026-07-01). Coalesce into one
  // rAF so N keystrokes in a frame trigger at most ONE resize, after the value
  // has already painted, so typing itself stays instant.
  const growRaf = useRef<number | null>(null);
  const autoGrow = useCallback(() => {
    if (growRaf.current != null) cancelAnimationFrame(growRaf.current);
    growRaf.current = requestAnimationFrame(() => {
      growRaf.current = null;
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    });
  }, []);
  useEffect(() => () => { if (growRaf.current != null) cancelAnimationFrame(growRaf.current); }, []);
  // Auto-grow also on PROGRAMMATIC input changes (Issue #222): draft restore on
  // return, prefill, reviewEdit set the text via setInput — without this the
  // multi-line text sat in a 1-row box until the next keystroke. rAF-coalesced,
  // so the per-keystroke path stays free.
  useLayoutEffect(() => { autoGrow(); }, [input, sessionName, autoGrow]);


  // "Optimistic busy": the moment we POST the message we know the agent
  // is busy by definition — don't wait for the backend pane-parser to
  // notice. Drives the Stop button so the user can interrupt
  // immediately, before agentBusy from the backend catches up.
  const [optimisticBusy, setOptimisticBusy] = useState(false);
  const optimisticTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Backend has confirmed activity → drop the optimistic flag, the real
  // agentBusy now drives the Stop button. When the agent goes back to
  // idle, agentBusy turns false, the Stop disappears as before.
  useEffect(() => {
    if (agentBusy && optimisticBusy) {
      setOptimisticBusy(false);
      if (optimisticTimerRef.current) clearTimeout(optimisticTimerRef.current);
    }
  }, [agentBusy, optimisticBusy]);

  const send = useCallback(() => {
    if (sendingRef.current) return;
    if (canSubmit === false) return; // gated (e.g. collab needs recipients)
    // Läuft noch ein Upload, wird die Absicht gemerkt statt der Anhang verloren.
    if (uploading) { setPendingSend('send'); return; }
    const attachUrls = attachments.map((a) => a.url).join(' ');
    const combined = [input.trim(), attachUrls].filter(Boolean).join('\n');
    if (!combined) return;
    sendingRef.current = true;
    setInput('');
    setAttachments([]);
    resetTextarea();
    saveDraft(sessionName, '');
    onSending?.(true);
    // Show Stop button immediately. Safety-net timeout: if the backend
    // never sees activity (very rare — usually means the agent finished
    // before our poll caught it) we drop optimistic after 90 s so the
    // button doesn't stick around for nothing.
    setOptimisticBusy(true);
    if (optimisticTimerRef.current) clearTimeout(optimisticTimerRef.current);
    optimisticTimerRef.current = setTimeout(() => setOptimisticBusy(false), 90_000);
    // Route through the outbox — if the POST fails (offline, server
    // down) the message stays in localStorage and retries automatically
    // when connectivity returns. No more silent loss.
    submit(combined);
    triggerSentFlash();
    sendingRef.current = false;
  }, [input, attachments, sessionName, onSending, submit, triggerSentFlash, canSubmit, uploading]);

  // Long-press → enqueue. The message stays local, the textarea clears,
  // and the auto-flush effect below dispatches it once agentBusy goes
  // false. Repeated long-presses append, so the user can stack a few
  // thoughts before the agent is ready.
  const queueSend = useCallback(() => {
    if (uploading) { setPendingSend('queue'); return; }
    const attachUrls = attachments.map((a) => a.url).join(' ');
    const combined = [input.trim(), attachUrls].filter(Boolean).join('\n');
    if (!combined) return;
    setQueuedMessages((prev) => ({
      ...prev,
      [sessionName]: [...(prev[sessionName] || []), combined],
    }));
    setInput('');
    setAttachments([]);
    resetTextarea();
    saveDraft(sessionName, '');
  }, [input, attachments, sessionName, resetTextarea, uploading]);

  // "Über Prompt Agent" — the prompt becomes a structured Issue/Goal via issue-api
  // /intake, with the CURRENT chat agent as context_agent so the assignee defaults to the
  // agent whose chat this is — no freetext guessing (Alex's correction, #147). The
  // PromptAgent *bot* is decommissioned; this is its headless endpoint. Same option for
  // typed text (send-menu) and STT (mic long-press → applied after transcribe).
  // Prompt-Agent flash → shared toast store (#194), top-right like everything else:
  // info/progress + success auto-hide, errors stay until clicked. dedupKey='pa-flash'
  // so a new flash replaces the old (e.g. "Sende…" → "✓ Issue angelegt").
  const showPaFlash = useCallback((msg: string, kind: 'ok' | 'err' | 'info' | 'pending') => {
    dismissToastByKey('pa-flash');
    pushToast({
      dedupKey: 'pa-flash',
      // pending = amber/yellow intermediate ("Sende an Prompt Agent…", #201); ok=green,
      // err=red, else info=blue.
      variant: kind === 'ok' ? 'success' : kind === 'err' ? 'error' : kind === 'pending' ? 'pending' : 'info',
      // pending persists until the outcome (✓ / error / chat reply) replaces it;
      // errors stay until clicked; the rest auto-hide.
      dismiss: kind === 'err' ? 'manual' : kind === 'pending' ? 'persist' : 'auto',
      autoHideMs: kind === 'ok' ? 5000 : 4500,
      content: msg,
    });
  }, []);
  // Route to the Prompt-Agent for an EXPLICIT target agent. The voice flow locks
  // the target at record-start and passes it here, so switching tabs during
  // transcription can never misroute (Alex, 2026-07-01). `routeToPromptAgent`
  // is the convenience wrapper that uses the currently-viewed session.
  // `position` (Issue #943): 'front' reiht das neue Issue VOR die wartenden,
  // damit es als Nächstes drankommt. Default bleibt 'back' — bestehende
  // Aufrufe dürfen ihre Bedeutung nicht still ändern.
  const routeToPromptAgentFor = useCallback(async (text: string, target: string, position?: 'front' | 'back') => {
    showPaFlash(position === 'front' ? 'Sende an Prompt Agent (als Nächstes)…' : 'Sende an Prompt Agent…', 'pending');
    try {
      const r = await api.intake.submit(text, target, position);
      if (r.intent === 'chat') {
        showPaFlash(`💬 ${r.reply || '(keine Antwort)'}`, 'info');
      } else {
        const label = r.intent === 'goal' ? 'Goal' : 'Issue';
        const who = r.assignee ? ` · ${r.assignee.replace(/^agent:/, '')}` : '';
        const pos = position === 'front' ? ' · vorgereiht' : '';
        showPaFlash(`✓ ${label} #${r.id} angelegt${who}${pos}`, 'ok');
      }
    } catch (e) {
      showPaFlash(`⚠ ${e instanceof Error ? e.message : 'Fehlgeschlagen'}`, 'err');
    }
  }, [showPaFlash]);
  const routeToPromptAgent = useCallback(
    (text: string, position?: 'front' | 'back') => routeToPromptAgentFor(text, sessionName, position),
    [routeToPromptAgentFor, sessionName],
  );

  // Führt eine während des Uploads gemerkte Sende-Absicht aus, sobald alle
  // Uploads durch sind.
  //
  // Bei einem FEHLGESCHLAGENEN Upload wird bewusst NICHT gesendet: sonst ginge
  // der Text doch ohne Bild raus — also genau der stille Verlust, den diese
  // Änderung beseitigt. Der Nutzer sieht die Fehlermeldung, behält Text und
  // Anhänge und entscheidet selbst (erneut anhängen oder ohne Bild senden).
  useEffect(() => {
    if (!pendingSend) return;
    const decision = dispatchDecision({ uploading, uploadError });
    if (decision === 'wait') return;
    setPendingSend(null);
    if (decision === 'abort') return;
    if (pendingSend === 'send') send();
    else if (pendingSend === 'queue') queueSend();
    else if (pendingSend === 'promptAgent') promptAgentSend();
    else if (pendingSend === 'promptAgentFront') promptAgentSend('front');
    else if (pendingSend === 'reviewSend') reviewSend();
    else if (pendingSend === 'reviewQueue') reviewQueue();
    else reviewPromptAgent();
    // `send`/`queueSend`/`promptAgentSend` hängen an `uploading`; der Effekt
    // läuft erst, wenn das false ist — die Callbacks sind dann die neuen und
    // sehen die vollständige Anhangsliste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSend, uploading, uploadError]);

  const promptAgentSend = useCallback((position?: 'front' | 'back') => {
    if (sendingRef.current) return;
    if (uploading) { setPendingSend(position === 'front' ? 'promptAgentFront' : 'promptAgent'); return; }
    const attachUrls = attachments.map((a) => a.url).join(' ');
    const combined = [input.trim(), attachUrls].filter(Boolean).join('\n');
    if (!combined) return;
    setInput('');
    setAttachments([]);
    resetTextarea();
    saveDraft(sessionName, '');
    void routeToPromptAgent(combined, position);
    triggerSentFlash();
  }, [input, attachments, sessionName, resetTextarea, routeToPromptAgent, triggerSentFlash, uploading]);

  // Mic long-press → route THIS recording to the review screen after transcribe, so
  // the operator picks Senden / Queue / Prompt Agent AFTER recording (Alex, mobile —
  // a touch long-press menu is hijacked by iOS text-selection; plain taps on the
  // review screen are robust). forceReviewRef forces review even when auto-send is on.
  const forceReviewRef = useRef(false);
  // Voice → Prompt-Agent flow (Alex, 2026-07-01): a long-press records a message
  // for the Prompt-Agent. The TARGET agent is locked at record-start (ref, not
  // stale) so switching tabs during transcription can't misroute; a toggle next
  // to the waveform picks the route (default Prompt-Agent, or Queue). On stop the
  // recording auto-routes to the locked target — no review dialog. Refs mirror the
  // state so rec.onstop (a closure from record-start) reads the CURRENT values.
  // Semantics (Alex 2026-07-03, replaces the old promptagent|queue ROUTE choice):
  // long-press = ALWAYS Prompt-Agent. "Queue" is an on/off MODIFIER — with Queue ON
  // the prompt is parked and only submitted to the Prompt-Agent once the target agent
  // is idle (so a busy agent isn't interrupted because of an earlier prompt).
  const [voiceQueueOn, setVoiceQueueOn] = useState(false);
  const voiceQueueOnRef = useRef(false);
  const [voiceRouteActive, setVoiceRouteActive] = useState(false);
  const voiceRouteActiveRef = useRef(false);
  // Sessions whose queued text should flush via Prompt-Agent (not as direct message).
  const [queuedViaPa, setQueuedViaPa] = useState<Record<string, boolean>>({});
  const recordingTargetRef = useRef(sessionName);
  // Attachment URLs frozen at record-start (Issue #217) — the route-locked send
  // includes them even if the user switches tabs during transcription.
  const recordingAttachUrlsRef = useRef('');
  const setQueueOn = useCallback((on: boolean) => { voiceQueueOnRef.current = on; setVoiceQueueOn(on); }, []);
  const micTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const micLongPressRef = useRef(false);
  // Long-press movement tolerance — iOS fires touchmove on sub-pixel finger jitter,
  // which was cancelling the press timer before it could fire (= "no menu on touch").
  const micTouchStartRef = useRef<{ x: number; y: number } | null>(null);
  const sendTouchStartRef = useRef<{ x: number; y: number } | null>(null);

  // Explicitly release the current session's parked message before the agent
  // becomes idle. This is intentionally a separate action from `send()`: the
  // composer may already be empty, while the queued text still has to keep its
  // original direct-vs-Prompt-Agent route.
  const pushQueuedNow = useCallback(() => {
    if (!queuedText || sendingRef.current) return;
    const viaPa = !!queuedViaPa[sessionName];
    sendingRef.current = true;
    clearQueuedForSession();
    onSending?.(true);
    setOptimisticBusy(true);
    if (optimisticTimerRef.current) clearTimeout(optimisticTimerRef.current);
    optimisticTimerRef.current = setTimeout(() => setOptimisticBusy(false), 90_000);
    if (viaPa) {
      void routeToPromptAgentFor(queuedText, sessionName);
    } else {
      // Same persisted outbox as a regular send: an immediate push is still
      // retry-safe if the network drops at this exact moment.
      submit(queuedText);
    }
    triggerSentFlash();
    sendingRef.current = false;
  }, [queuedText, queuedViaPa, sessionName, clearQueuedForSession, onSending,
    routeToPromptAgentFor, submit, triggerSentFlash]);

  // Auto-flush the current session's queued message once the agent is
  // idle. Small debounce so we don't race the optimistic-busy clear right
  // after the previous send. If the user cancels the queue mid-wait, this
  // session's entry is removed and the effect no-ops on next change.
  useEffect(() => {
    // Only ever flush the CURRENTLY-viewed session's queue, and only when
    // THIS agent is idle. A queue parked for another agent stays put until
    // that agent is open + green — it can never land on the wrong one.
    const pendingList = queuedMessages[sessionName];
    const text = pendingList?.length ? pendingList.join('\n\n') : '';
    if (!text) return;
    if (agentBusy || optimisticBusy) return;
    const t = setTimeout(() => {
      if (sendingRef.current) return;
      sendingRef.current = true;
      onSending?.(true);
      setOptimisticBusy(true);
      if (optimisticTimerRef.current) clearTimeout(optimisticTimerRef.current);
      optimisticTimerRef.current = setTimeout(() => setOptimisticBusy(false), 90_000);
      // Drop only this session's entry.
      setQueuedMessages((prev) => {
        if (!(sessionName in prev)) return prev;
        const { [sessionName]: _gone, ...rest } = prev;
        void _gone;
        return rest;
      });
      // enqueueAndSend binds to the current sessionName — which equals the
      // queued entry's session here, since we only flush the current one.
      // Also outbox-routed → offline-safe (persists if the network drops
      // exactly when the agent goes green).
      if (queuedViaPa[sessionName]) {
        // Voice-queued FOR the Prompt-Agent (queue = timing modifier, not a route):
        // now that the agent is idle, run the intake instead of a direct send.
        setQueuedViaPa((prev) => { const { [sessionName]: _g, ...rest } = prev; void _g; return rest; });
        void routeToPromptAgentFor(text, sessionName);
      } else {
        submit(text);
      }
      sendingRef.current = false;
    }, 600);
    return () => clearTimeout(t);
  }, [queuedMessages, queuedViaPa, agentBusy, optimisticBusy, sessionName, onSending, submit, routeToPromptAgentFor]);

  // BACKGROUND queue flush (Alex 2026-07-03): queues are keyed by ORIGIN agent and
  // used to flush only while viewing that agent. The federated session.state events
  // (#212) now tell us when ANY agent goes ready — flush its queued entry from
  // wherever we are: to the Prompt-Agent (queuedViaPa) or as a direct send to that
  // agent (api.sessions.sendInput, NOT the current-session outbox).
  const bgFlushInFlight = useRef<Set<string>>(new Set());
  const queuedRef = useRef(queuedMessages);
  queuedRef.current = queuedMessages;
  const queuedViaPaRef = useRef(queuedViaPa);
  queuedViaPaRef.current = queuedViaPa;
  useEffect(() => {
    const bus = getSseBus();
    return bus.subscribe('session.state', (ev) => {
      const name = (ev as { name?: string; state?: string }).name;
      const state = (ev as { name?: string; state?: string }).state;
      if (!name || (state !== 'ready' && state !== 'idle')) return;
      if (name === sessionName) return; // current view: the existing idle-flush handles it
      const pending = queuedRef.current[name];
      const text = pending?.length ? pending.join('\n\n') : '';
      if (!text || bgFlushInFlight.current.has(name)) return;
      bgFlushInFlight.current.add(name);
      const viaPa = !!queuedViaPaRef.current[name];
      setQueuedMessages((prev) => {
        if (!(name in prev)) return prev;
        const { [name]: _gone, ...rest } = prev; void _gone; return rest;
      });
      setQueuedViaPa((prev) => {
        if (!(name in prev)) return prev;
        const { [name]: _gone, ...rest } = prev; void _gone; return rest;
      });
      const run = viaPa
        ? routeToPromptAgentFor(text, name)
        : api.sessions.sendInput(name, text).then(() => undefined);
      void Promise.resolve(run)
        .catch(() => {
          // Failed (net) → put it back so it retries on the next ready-event.
          // Zurück in die Warteschlange, und zwar VOR das inzwischen
          // Hinzugekommene: Die ältere Nachricht war zuerst da.
          setQueuedMessages((prev) => ({ ...prev, [name]: [...(pending || []), ...(prev[name] || [])] }));
          if (viaPa) setQueuedViaPa((prev) => ({ ...prev, [name]: true }));
        })
        .finally(() => { bgFlushInFlight.current.delete(name); });
    });
  }, [sessionName, routeToPromptAgentFor]);

  const sendKey = useCallback(
    (key: string) => {
      if (key === '/resume') {
        api.sessions.sendInput(sessionName, '/resume');
      } else {
        api.sessions.sendKey(sessionName, key);
      }
    },
    [sessionName]
  );

  const stopAgent = useCallback(() => {
    api.sessions.sendKey(sessionName, 'Escape');
    setTimeout(() => api.sessions.sendKey(sessionName, 'Escape'), 150);
  }, [sessionName]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // ── Voice Recording ──────────────────────────────────────────
  const startRecording = async () => {
    // Lock the recording's origin agent + its attachments NOW (both tap + long-press) —
    // used for the agent-aware recording UI and, on the long-press path, for routing
    // (attachments included + cleared after dispatch, Issue #217).
    recordingTargetRef.current = sessionName;
    recordingAttachUrlsRef.current = attachments.map((a) => a.url).join(' ');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;

      // VU-equalizer: AnalyserNode + FFT splits the live mic stream into
      // frequency bands. Each bar height = average amplitude in its band.
      // Voice produces a moving spectral signature so the bars dance
      // vertically as you speak — much more obviously "mic is active"
      // than a static row of pulse-lit threshold dots.
      try {
        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = new Ctx();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 128; // 64 frequency bins
        analyser.smoothingTimeConstant = 0.6;
        source.connect(analyser);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
        const freqBuf = new Uint8Array(analyser.frequencyBinCount);
        // Skip the very lowest bins — those carry DC + sub-bass rumble
        // unrelated to voice and would bias the leftmost bar.
        const SKIP = 2;
        const usableBins = freqBuf.length - SKIP;
        const binsPerBar = Math.max(1, Math.floor(usableBins / VU_BAR_COUNT));
        const tick = () => {
          if (!analyserRef.current) return;
          analyserRef.current.getByteFrequencyData(freqBuf);
          const bands: number[] = [];
          for (let b = 0; b < VU_BAR_COUNT; b++) {
            let sum = 0;
            const start = SKIP + b * binsPerBar;
            const end = Math.min(freqBuf.length, start + binsPerBar);
            for (let i = start; i < end; i++) sum += freqBuf[i];
            const avg = sum / (end - start) / 255; // 0..1
            // sqrt for perceptual flattening (voice mids would
            // dominate everything otherwise).
            bands.push(Math.min(1, Math.sqrt(avg) * 1.15));
          }
          setAudioBands(bands);
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch { /* AudioContext unsupported on this browser — bar still works without VU */ }

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/mp4';
      const rec = new MediaRecorder(stream, { mimeType });
      mediaRecRef.current = rec;
      chunksRef.current = [];

      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (timerRef.current) clearInterval(timerRef.current);
        // Tear down VU-meter: cancel the rAF loop first so no late
        // setState fires after we close the AudioContext.
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        analyserRef.current = null;
        if (audioCtxRef.current) {
          try { void audioCtxRef.current.close(); } catch { /* already closed */ }
          audioCtxRef.current = null;
        }
        setAudioBands(new Array(VU_BAR_COUNT).fill(0));

        // Cancelled via X — drop the recording without transcribing.
        // The final flushed chunk re-populated chunksRef after the
        // cancel handler cleared it, so we must gate here, not there.
        if (cancelledRef.current) {
          cancelledRef.current = false;
          chunksRef.current = [];
          setMicState('idle');
          return;
        }

        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size < 500) {
          setMicState('idle');
          return;
        }

        setMicState('transcribing');
        try {
          // Filename must match the ACTUAL container: the backend derives the
          // stored file's suffix from it. We used to always send
          // 'recording.webm' — on Safari that put mp4 content under a .webm
          // name (Whisper sniffs the container, so it worked, but the file on
          // disk was mislabelled). AppDev's iOS client does it correctly with
          // .m4a; aligning to him rather than the other way round.
          const ext = mimeType.startsWith('audio/mp4') ? 'm4a' : 'webm';
          const data = await api.transcribe(blob, `recording.${ext}`);
          if (data.text) {
            if (voiceRouteActiveRef.current) {
              // Long-press (Prompt-Agent) recording: route the transcription to the
              // agent LOCKED at record-start, per the toggle's route — NO review
              // dialog. Target + route + ATTACHMENTS come from refs frozen at record-
              // start, so a tab-switch during transcription can't misroute (Alex,
              // 2026-07-01). We deliberately drop the typed draft (may belong to a
              // different session after a tab-switch) — but the attachments visible
              // when the recording started DO belong to the message: include their
              // URLs and clear the chips after dispatch (Issue #217: "vergisst das
              // Thumbnail und löscht es auch nicht weg").
              const target = recordingTargetRef.current;
              // Der Schnappschuss vom Aufnahmestart kann Uploads verpassen, die
              // WÄHREND der Aufnahme fertig wurden. Beide Quellen vereinigen
              // (dedupliziert) statt eine davon zu verlieren — die Sperre auf den
              // Ziel-Agenten aus #217 bleibt davon unberührt.
              const allUrls = mergeAttachUrls(recordingAttachUrlsRef.current, attachments.map((a) => a.url));
              const combined = [data.text.trim(), allUrls].filter(Boolean).join('\n');
              if (allUrls) setAttachments([]);
              recordingAttachUrlsRef.current = '';
              setMicState('idle');
              if (voiceQueueOnRef.current) {
                // Queue ON: park it — flushes to the PROMPT-AGENT once the agent is idle.
                setQueuedMessages((prev) => ({
                  ...prev,
                  [target]: [...(prev[target] || []), combined],
                }));
                setQueuedViaPa((prev) => ({ ...prev, [target]: true }));
                showPaFlash(`⏱ Wartet — geht an den Prompt-Agent sobald ${target.replace(/^agent:/, '')} frei ist`, 'info');
              } else {
                void routeToPromptAgentFor(combined, target);
                triggerSentFlash();
              }
            } else {
              // Tap path — unchanged. Auto-send to the current agent (or review if
              // the autosend setting is off / forceReview is set).
              const autoSend = localStorage.getItem('cloud-voice-autosend') !== 'false' && !forceReviewRef.current;
              forceReviewRef.current = false;
              // Läuft noch ein Upload, wird NICHT automatisch gesendet: die
              // Transkription landet stattdessen in der Review-Ansicht, deren
              // Sende-Knöpfe den Upload abwarten. Lieber eine Bestätigung mehr
              // als eine Sprachnachricht ohne das Bild.
              if (autoSend && !uploading) {
                const attachUrls = attachments.map((a) => a.url).join(' ');
                const parts = [input.trim(), data.text, attachUrls].filter(Boolean);
                const combined = parts.join('\n');
                setInput('');
                resetTextarea();
                setAttachments([]);
                saveDraft(sessionName, '');
                setMicState('idle');
                onSending?.(true);
                submit(combined);
              } else {
                setReviewText(data.text);
                setMicState('review');
              }
            }
          } else {
            setMicState('idle');
          }
        } catch (err) {
          // BENANNT scheitern (Alex 18.08. auf Davids Instanz: „ich sehe, dass
          // es einfach abbricht"). Vorher färbte sich nur der Mikrofon-Knopf
          // rot — auf dem Handy praktisch unsichtbar. Häufigste Ursache auf
          // einer Kundeninstanz: die Sprachschicht ist dort nicht eingerichtet
          // (kein KI-Konto) → der Server lehnt ab, und der Nutzer sah nichts.
          setMicState('error');
          const msg = err instanceof Error ? err.message : String(err);
          const status = (err as { status?: number } | null)?.status;
          showPaFlash(
            status === 404 || /transcribe 40[0-9]/.test(msg)
              ? 'Diktat nicht verfügbar — auf dieser Instanz ist keine Sprachschicht eingerichtet.'
              : `Diktat fehlgeschlagen: ${msg}`,
            'err',
          );
          // …und ins zentrale Protokoll, mit der Instanz am Eintrag. Der Hinweis
          // am Bildschirm hilft dem, der davorsitzt; die Fehlersuche aus der
          // Ferne braucht die Zeile (Alex 18.08.: „dann könntest du eine
          // tenant-spezifische Abfrage machen und einfach den Fehler anschauen").
          void logToApi('CloudV2-diag', 'Error', `Diktat gescheitert: ${msg}`, {
            status: status ?? null,
            bytes: blob.size,
            mime: mimeType,
            agent: sessionName,
          });
        }
      };

      cancelledRef.current = false;
      setVoiceSendPressed(false);
      setGalleryIndex(0);
      rec.start(1000);
      setMicState('recording');
      setMicTime(0);
      timerRef.current = setInterval(() => setMicTime((t) => t + 1), 1000);
    } catch {
      setMicState('error');
    }
  };

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const rec = mediaRecRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.stop();
    } else {
      // Fallback: if recorder is already inactive, clean up
      streamRef.current?.getTracks().forEach((t) => t.stop());
      // Also tear down the VU-meter chain — rec.onstop won't fire in this branch.
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      analyserRef.current = null;
      if (audioCtxRef.current) {
        try { void audioCtxRef.current.close(); } catch { /* no-op */ }
        audioCtxRef.current = null;
      }
      setAudioBands(new Array(VU_BAR_COUNT).fill(0));
      setMicState('idle');
    }
  };

  const handleMicClick = () => {
    if (micState === 'idle' || micState === 'error') {
      // Plain tap = NOT a route-locked Prompt-Agent recording — keep old behavior.
      voiceRouteActiveRef.current = false;
      setVoiceRouteActive(false);
      startRecording();
    } else if (micState === 'recording') stopRecording();
  };

  const reviewSend = () => {
    if (uploading) { setPendingSend('reviewSend'); return; }
    if (sendingRef.current) return;
    const attachUrls = attachments.map((a) => a.url).join(' ');
    const parts = [input.trim(), reviewText, attachUrls].filter(Boolean);
    const combined = parts.join('\n');
    sendingRef.current = true;
    setReviewText('');
    setInput('');
    resetTextarea();
    setAttachments([]);
    setMicState('idle');
    onSending?.(true);
    submit(combined);
    sendingRef.current = false;
  };

  const reviewQueue = () => {
    if (uploading) { setPendingSend('reviewQueue'); return; }
    const attachUrls = attachments.map((a) => a.url).join(' ');
    const combined = [input.trim(), reviewText, attachUrls].filter(Boolean).join('\n');
    if (!combined) return;
    setQueuedMessages((prev) => ({
      ...prev,
      [sessionName]: [...(prev[sessionName] || []), combined],
    }));
    setReviewText(''); setInput(''); resetTextarea(); setAttachments([]); setMicState('idle');
  };

  const reviewPromptAgent = () => {
    if (uploading) { setPendingSend('reviewPromptAgent'); return; }
    const attachUrls = attachments.map((a) => a.url).join(' ');
    const combined = [input.trim(), reviewText, attachUrls].filter(Boolean).join('\n');
    if (!combined) return;
    setReviewText(''); setInput(''); resetTextarea(); setAttachments([]); setMicState('idle');
    void routeToPromptAgent(combined);
    triggerSentFlash();
  };

  const reviewEdit = () => {
    const combined = input ? `${input}\n${reviewText}` : reviewText;
    setInput(combined);
    setReviewText('');
    setMicState('idle');
  };

  const reviewDiscard = () => {
    setReviewText('');
    setMicState('idle');
  };

  // ── MCP ──────────────────────────────────────────────────────
  const ALL_MCP = ALL_MCP_SERVERS as unknown as string[];

  const openMcpEditor = async () => {
    setShowMenu(false);
    try {
      const data = await api.sessions.getMcp(sessionName);
      const state: Record<string, boolean> = {};
      for (const s of ALL_MCP) state[s] = s in data.servers;
      setMcpServers(state);
      setShowMcp(true);
    } catch {
      setMcpServers({});
      setShowMcp(true);
    }
  };

  const saveMcp = async () => {
    const selected = ALL_MCP.filter((s) => mcpServers[s]);
    await api.sessions.setMcp(sessionName, selected).catch(() => {});
    setShowMcp(false);
  };

  // ── TTS ──────────────────────────────────────────────────────
  // Default = native (Web Speech / Siri — free, instant, no API call).
  // OpenAI is opt-in via long-press menu since it costs per request.
  const stopTTS = () => {
    // Native: cancel current + queued utterances. Safe to call when idle.
    // Also fires across an F5 reload — the marker restored the speaking
    // state so this Stop reaches the still-running OS speech engine.
    try { window.speechSynthesis.cancel(); } catch { /* no-op */ }
    clearNativeTtsMarker();
    // OpenAI: pause + reset audio element, revoke its blob URL.
    if (ttsAudioRef.current) {
      try {
        ttsAudioRef.current.pause();
        ttsAudioRef.current.currentTime = 0;
      } catch { /* no-op */ }
    }
    setIsSpeaking(false);
    setTtsPaused(false);
    setTtsKind(null);
    setTtsStatus('');
  };

  // OpenAI-wave playback controls (only meaningful for the seekable
  // HTMLAudioElement path). Pause/resume toggle + 15 s rewind so the
  // user can re-hear a passage they missed.
  const togglePauseTTS = () => {
    const a = ttsAudioRef.current;
    if (!a) return;
    if (a.paused) { void a.play().catch(() => {}); setTtsPaused(false); }
    else { a.pause(); setTtsPaused(true); }
  };
  const rewindTTS = (seconds: number) => {
    const a = ttsAudioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, a.currentTime - seconds);
    // If it was paused, a rewind implies the user wants to hear it again.
    if (a.paused) { void a.play().catch(() => {}); setTtsPaused(false); }
  };

  const handleTTS = () => {
    setShowMenu(false);
    // Re-click while speaking = stop. Indicator visible via Square icon
    // + .speaking button class so users know the toggle is live.
    if (isSpeaking) {
      stopTTS();
      return;
    }
    if (ttsMode === 'openai-compact' || ttsMode === 'openai-prose') {
      handleOpenAITTS(ttsMode === 'openai-prose' ? 'prose' : 'compact');
    } else {
      handleNativeTTS();
    }
  };

  // Native TTS: fetch last agent text, strip markdown, speak via Web
  // Speech API. Works on iOS Safari (uses Siri voice), Android Chrome,
  // and desktop. No backend call.
  const handleNativeTTS = async () => {
    // iOS Safari + Mac Safari: speak() must be called inside the user-
    // gesture window. The async fetch below burns that window, so we
    // fire a zero-volume placeholder utterance FIRST — synchronously,
    // still within the click handler — to keep the speech engine
    // "authorised". The real utterance gets queued after cancel().
    // Also primes the voice list — getVoices() often returns [] on
    // the very first call until the engine has spoken at least once.
    try {
      const primer = new SpeechSynthesisUtterance(' ');
      primer.volume = 0;
      primer.lang = 'de-DE';
      window.speechSynthesis.speak(primer);
    } catch { /* engine missing — fall through, real speak will fail loudly */ }

    setTtsStatus('Loading conversation since your last message…');
    try {
      const data = await api.sessions.v2(sessionName, 50);
      const turns = data.turns || [];
      // Same boundary logic as the OpenAI path: walk back to the user's
      // own direct/queue turn and read everything since (inclusive
      // agent reply + all subsequent iacp/queue/system pushes).
      const bundle = collectSpeakableSinceLastUserTurn(turns);
      const text = bundle.text;
      if (!text.trim()) {
        setTtsStatus('No text to speak');
        setTimeout(() => setTtsStatus(''), 2500);
        return;
      }
      if (bundle.turnCount > 1) {
        setTtsStatus(`Speaking ${bundle.turnCount} turns since your last message…`);
      }
      // Strip markdown, code fences, links
      const clean = text
        .replace(/```[\s\S]*?```/g, '')
        .replace(/`[^`]*`/g, '')
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/[#*_~>\-|]/g, '')
        .trim();
      if (!clean) {
        setTtsStatus('No text to speak');
        setTimeout(() => setTtsStatus(''), 2500);
        return;
      }
      window.speechSynthesis.cancel();
      const spokenText = clean.slice(0, 1500);
      const utterance = new SpeechSynthesisUtterance(spokenText);
      utterance.lang = 'de-DE';
      // Wait for voices to populate — getVoices() returns [] on cold
      // engines (Chrome / Firefox always; Safari sometimes). Capped at
      // 800 ms so we never block visibly.
      let voices = window.speechSynthesis.getVoices();
      if (voices.length === 0) {
        await new Promise<void>((resolve) => {
          const done = () => { voices = window.speechSynthesis.getVoices(); resolve(); };
          const timer = setTimeout(done, 800);
          window.speechSynthesis.addEventListener(
            'voiceschanged',
            () => { clearTimeout(timer); done(); },
            { once: true }
          );
        });
      }
      const deVoice = voices.find((v) => v.lang.startsWith('de'));
      if (deVoice) utterance.voice = deVoice;
      if (voices.length === 0) {
        // No engine at all — speak() will silently no-op, tell the user.
        setTtsStatus('Keine Sprachausgabe verfügbar (Browser/OS).');
        setTimeout(() => setTtsStatus(''), 4000);
        setIsSpeaking(false);
        return;
      }
      // Preview what Siri is about to read — first 70 chars so user can
      // verify the extraction picked the right text.
      const preview = spokenText.replace(/\s+/g, ' ').slice(0, 70);
      // Estimated playback duration for the F5-persistence marker.
      // German TTS ≈ 14 chars/s; pad 2 s for engine latency.
      const estMs = Math.min(10 * 60_000, (spokenText.length / 14) * 1000 + 2000);
      utterance.onstart = () => {
        setIsSpeaking(true);
        setTtsKind('native');
        setTtsStatus(`🔊 ${preview}${spokenText.length > 70 ? '…' : ''}`);
        setNativeTtsMarker({ sessionName, preview, expiresAt: Date.now() + estMs });
      };
      utterance.onend = () => {
        setIsSpeaking(false);
        setTtsKind(null);
        setTtsStatus('');
        clearNativeTtsMarker();
      };
      utterance.onerror = (e) => {
        setIsSpeaking(false);
        setTtsKind(null);
        clearNativeTtsMarker();
        // 'interrupted' = expected when user re-clicked to stop, swallow it.
        if (e.error === 'interrupted' || e.error === 'canceled') {
          setTtsStatus('');
          return;
        }
        setTtsStatus(`TTS error: ${e.error}`);
        setTimeout(() => setTtsStatus(''), 3000);
      };
      window.speechSynthesis.speak(utterance);
    } catch (e) {
      setTtsStatus(`TTS error: ${e instanceof Error ? e.message : '?'}`);
      setTimeout(() => setTtsStatus(''), 3000);
    }
  };

  // TTS failure pipeline → log-api via the shared client. The old
  // inline fetch had wrong body shape (`[...]` instead of `{entries:[...]}`)
  // AND was POSTing into a non-existent session (no auto-create) →
  // every line was silently dropped (Cloud's IACP 0087bd88). The shared
  // client ensures session-once + correct schema + surfaces 4xx.
  const logTts = (
    type: LogType,
    msg: string,
    extra?: Record<string, unknown>,
    stackTrace?: string,
  ) => {
    void logToApi('CloudV2-tts', type, msg, extra, stackTrace);
  };

  const handleOpenAITTS = async (style: 'compact' | 'prose' = 'compact') => {
    setTtsStatus(
      style === 'prose'
        ? 'Requesting OpenAI TTS (prose narration)…'
        : 'Requesting OpenAI TTS audio…'
    );

    // Unlock audio context synchronously in click handler (iOS requirement)
    const audio = new Audio();
    audio.src = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAABhgHkbKCAAAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAABhgHkbKCAAAAAAAAAAAAAAAAA';
    audio.play().catch(() => {});
    // Keep a ref so the playback-control bar (pause/resume + rewind)
    // can drive this element. isSpeaking stays true while the audio is
    // active (playing OR paused) so the controls remain visible; the
    // separate ttsPaused flag tracks the play/pause toggle.
    ttsAudioRef.current = audio;
    audio.onpause = () => { if (!audio.ended) setTtsPaused(true); };

    await new Promise((r) => setTimeout(r, 50));

    // Extract the latest assistant text from parsed turns. Mirrors the
    // native-TTS path: prefer kind: 'text', then unwrap kind: 'json'
    // (Guide pool personas etc.). Posting the text directly to
    // /api/tts-text bypasses the legacy tmux-scraping which raced with
    // mid-tool-call captures and returned spurious 422s.
    setTtsStatus('Loading conversation since your last message…');
    let speakText = '';
    let bundleSummary: SpeakBundle = { text: '', turnCount: 0, sinceUserTurn: false };
    try {
      const data = await api.sessions.v2(sessionName, 50);
      const turns = data.turns || [];
      bundleSummary = collectSpeakableSinceLastUserTurn(turns);
      speakText = bundleSummary.text;
    } catch (err) {
      // Surface the message so users with the console open can see the
      // cause (auth-expired, network, 5xx). Previously this catch swallowed
      // everything → blind diagnostic when Alex reported the bug 2026-06-01.
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[TTS] failed to load reply via api.sessions.v2:', err);
      logTts('Error', `v2-fetch failed: ${msg}`, { sessionName, lines: 50 });
      setTtsStatus(`TTS: failed to load reply (${msg.slice(0, 80)})`);
      setTimeout(() => setTtsStatus(''), 5000);
      return;
    }
    if (speakText.trim().length < 5) {
      console.warn('[TTS] no speakable text found in last turns; speakText=', JSON.stringify(speakText));
      logTts('Warning', 'no speakable text in last turns after tool-echo filter', { sessionName, speakText });
      setTtsStatus('TTS: no reply to read');
      setTimeout(() => setTtsStatus(''), 3000);
      return;
    }
    if (bundleSummary.turnCount > 1) {
      setTtsStatus(
        `Summarizing ${bundleSummary.turnCount} turns since your last message…`
      );
    } else {
      setTtsStatus('Summarizing last reply…');
    }

    const t0 = Date.now();
    try {
      const res = await api.ttsText({ text: speakText, voice: 'nova', lang: 'de', style });
      const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
      if (!res.ok) {
        // Pull whatever body the server returned for diagnostic — both
        // FastAPI 4xx (detail) and the legacy JSON {error} shape.
        let bodyHint = '';
        try {
          const txt = await res.text();
          if (txt) {
            try { const j = JSON.parse(txt); bodyHint = ` — ${j.detail || j.error || j.message || txt}`.slice(0, 120); }
            catch { bodyHint = ` — ${txt.slice(0, 120)}`; }
          }
        } catch { /* network read failed */ }
        console.error('[TTS] /api/tts-text non-OK:', res.status, bodyHint);
        logTts('Error', `HTTP ${res.status}${bodyHint}`, { sessionName, requestText: speakText.slice(0, 80) });
        setTtsStatus(`TTS failed: HTTP ${res.status}${bodyHint}`);
        setTimeout(() => setTtsStatus(''), 6000);
        return;
      }
      // X-TTS-Preview header carries the EXACT text OpenAI synthesized
      // (URL-encoded). Surface it so the user can verify what was
      // spoken — this is the diagnostic loop closure.
      const previewEnc = res.headers.get('x-tts-preview') || '';
      const summarized = res.headers.get('x-tts-summarized') === '1';
      let preview = '';
      try { preview = decodeURIComponent(previewEnc); } catch { preview = ''; }
      const blob = await res.blob();
      if (blob.size < 100) {
        console.warn('[TTS] received tiny blob, size=', blob.size, 'type=', blob.type);
        logTts('Warning', `empty audio blob (size=${blob.size}, type=${blob.type})`, { sessionName });
        setTtsStatus(`TTS: empty audio (size=${blob.size})`);
        setTimeout(() => setTtsStatus(''), 5000);
        return;
      }
      const url = URL.createObjectURL(blob);
      audio.src = url;
      audio.onplay = () => { setIsSpeaking(true); setTtsKind('openai'); setTtsPaused(false); };
      audio.onended = () => {
        URL.revokeObjectURL(url);
        setIsSpeaking(false);
        setTtsKind(null);
        setTtsPaused(false);
        setTtsStatus('');
      };
      const previewLine = preview
        ? `🔊${summarized ? ' (gpt)' : ''} ${preview.replace(/\s+/g, ' ').slice(0, 80)}${preview.length > 80 ? '…' : ''}`
        : `Playing ${(blob.size / 1024).toFixed(0)}KB (OpenAI, ${elapsed}s)`;
      setTtsStatus(previewLine);
      logTts('Log', `TTS played${summarized ? ' (gpt-summary)' : ''}: ${preview.slice(0, 120)}`, {
        sessionName,
        sizeBytes: blob.size,
        elapsedSeconds: Number(elapsed),
        summarized,
      });
      audio.play().catch((playErr) => {
        // Autoplay blocked — not really an error per se, but Alex wants
        // signal that the user is going to see "Tap anywhere to play".
        logTts('Warning', `autoplay blocked: ${playErr instanceof Error ? playErr.message : playErr}`, { sessionName });
        setTtsStatus('Tap anywhere to play (autoplay blocked)');
        const retry = () => { audio.play().catch(() => {}); setTtsStatus(previewLine); };
        document.addEventListener('click', retry, { once: true });
      });
    } catch (err) {
      const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
      const msg = err instanceof Error ? err.message : String(err);
      const stack = err instanceof Error ? err.stack : undefined;
      console.error('[TTS] /api/tts-text fetch threw after', elapsed, 's:', err);
      // Stack goes in the dedicated `stack_trace` field — log-api
      // dashboard renders it as a monospace code-block.
      logTts('Exception', `fetch threw after ${elapsed}s: ${msg}`, { sessionName }, stack);
      setTtsStatus(`TTS error after ${elapsed}s: ${msg}`);
      setTimeout(() => setTtsStatus(''), 6000);
    }
  };

  // ── File Upload ──────────────────────────────────────────────
  const handleUpload = () => {
    setShowMenu(false);
    fileInputRef.current?.click();
  };

  const processFiles = useCallback(async (input: File[] | FileList) => {
    if (!storageAvailable) return;
    const files = Array.from(input);
    if (!files.length) return;

    setUploadError('');
    setUploading(true);
    for (const file of files) {
      if (file.size > MAX_CHAT_UPLOAD_BYTES) {
        setUploadError(
          `${file.name} ist größer als 50 MB und kann im Chat nicht hochgeladen werden.`
        );
        continue;
      }

      // Create local preview for images
      let preview: string | undefined;
      if (file.type.startsWith('image/')) {
        preview = URL.createObjectURL(file);
      }

      try {
        const res = await api.uploadRaw('/api/upload', file);
        const responseText = await res.text();
        let data: { path?: string; url?: string; storage_id?: number; detail?: string; error?: string } = {};
        if (responseText) {
          try {
            data = JSON.parse(responseText);
          } catch {
            if (!res.ok) throw new Error(responseText.slice(0, 160));
          }
        }
        if (!res.ok) {
          // 401 ist hier kein Aufruf-Fehler, sondern ein Zustand: Die Anmeldung
          // trägt nicht mehr, und der Endpunkt-Text („authentication required")
          // sagt dem Nutzer nicht, was zu tun ist. Tommy hat das am 19.08.
          // dreimal wiederholt bekommen, ohne daraus ableiten zu können, dass er
          // sich neu anmelden muss. Der Client hat vorher schon einmal die
          // Token-Erneuerung versucht (api.uploadRaw) — ein 401 hier heißt also:
          // sie hat nicht geholfen.
          if (res.status === 401) {
            throw new Error('Anmeldung abgelaufen — bitte neu anmelden, dann den Upload wiederholen.');
          }
          throw new Error(data.detail || data.error || `HTTP ${res.status}`);
        }
        const url = data.url ?? data.path;
        if (url) {
          // For storage URLs, use thumbnail as preview
          const storageBase = serviceOrigin('storage');
          const thumb = data.storage_id && storageBase
            ? `${storageBase}/storage/media/${data.storage_id}?variant=thumbnail&format=jpg`
            : preview;
          setAttachments((prev) => [...prev, { url, preview: thumb, name: file.name }]);
        } else {
          throw new Error('Upload-Antwort enthält keine Datei-URL.');
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        setUploadError(`Upload von ${file.name} fehlgeschlagen: ${message}`);
      }
    }
    setUploading(false);
  }, []);

  // Exposed to AgentDetail so its container-level drop handler can deposit
  // files into the same attachments list the paperclip button populates.
  useImperativeHandle(ref, () => ({ addFiles: processFiles }), [processFiles]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    await processFiles(files);
    e.target.value = '';
  };

  const removeAttachment = (idx: number) => {
    setAttachments((prev) => {
      const removed = prev[idx];
      if (removed.preview?.startsWith('blob:')) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // ── URL Extractor ────────────────────────────────────────────
  const handleURLs = async () => {
    setShowMenu(false);
    try {
      const res = await fetch(`/api/sessions/${sessionName}/urls?lines=50`);
      const data = await res.json();
      const urls: string[] = data.urls || [];
      if (urls.length === 0) {
        alert('No URLs found');
        return;
      }
      const url = urls[urls.length - 1];
      window.open(url, '_blank');
    } catch {
      /* silent */
    }
  };

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // A recording/transcribe belongs to the agent it STARTED on. When you switch to
  // another agent mid-flight, its recording UI must NOT show here — it keeps running
  // in the background and lands on the origin agent (agent-aware, Alex 2026-07-01).
  const isRecordingOwner = sessionName === recordingTargetRef.current;

  // ── Review State ─────────────────────────────────────────────
  if (micState === 'review' && isRecordingOwner) {
    return (
      <div className="input-bar-wrap">
        {attachments.length > 0 && (
          <div className="attachment-row">
            {attachments.map((att, i) => (
              <div key={i} className="attachment-item">
                {att.preview ? (
                  <img src={att.preview} alt="" className="attachment-thumb" />
                ) : (
                  <div className="attachment-file">{att.name.split('.').pop()}</div>
                )}
                <button className="attachment-remove" onClick={() => removeAttachment(i)}><X size={12} /></button>
              </div>
            ))}
          </div>
        )}
        <div className="review-bar">
          {input && <div className="review-prefix">{input}</div>}
          <div className="review-text">{reviewText}</div>
          <div className="review-actions">
            <button className="review-btn review-discard" onClick={reviewDiscard}>
              Verwerfen
            </button>
            <button className="review-btn review-edit" onClick={reviewEdit}>
              Bearbeiten
            </button>
            {/* Order (Issue #188): Senden · Queue · Prompt-Agent(groß). The
                big/primary slot moved from Senden to Prompt-Agent. */}
            <button className="review-btn review-send" onClick={reviewSend}>
              Senden
            </button>
            <button className="review-btn review-queue" onClick={reviewQueue}>
              Queue
            </button>
            <button className="review-btn review-promptagent" onClick={reviewPromptAgent} title="Über den Prompt-Agent veredeln + weiterleiten">
              Prompt-Agent
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Recording State ──────────────────────────────────────────
  if (micState === 'recording' && isRecordingOwner) {
    const imgAttachments = attachments.filter((a) => a.preview);
    const gi = Math.min(galleryIndex, Math.max(0, imgAttachments.length - 1));
    const current = imgAttachments[gi];
    return (
      <div className="input-bar-wrap">
        {/* Image gallery — keep attachments visible while recording so the
            user can look at (and swipe between) the pictures they're
            narrating. Larger than the idle-state thumbnails on purpose. */}
        {imgAttachments.length > 0 && current && (
          <div className="recording-gallery">
            <div
              className="recording-gallery-stage"
              onTouchStart={(e) => { galleryTouchX.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                if (galleryTouchX.current === null) return;
                const dx = e.changedTouches[0].clientX - galleryTouchX.current;
                galleryTouchX.current = null;
                if (Math.abs(dx) < 40) return;
                if (dx < 0) setGalleryIndex(Math.min(imgAttachments.length - 1, gi + 1));
                else setGalleryIndex(Math.max(0, gi - 1));
              }}
            >
              {imgAttachments.length > 1 && (
                <button
                  type="button"
                  className="recording-gallery-nav prev"
                  onClick={() => setGalleryIndex(Math.max(0, gi - 1))}
                  disabled={gi === 0}
                  title="Vorheriges Bild"
                >‹</button>
              )}
              <img src={current.preview} alt="" className="recording-gallery-img" />
              {imgAttachments.length > 1 && (
                <button
                  type="button"
                  className="recording-gallery-nav next"
                  onClick={() => setGalleryIndex(Math.min(imgAttachments.length - 1, gi + 1))}
                  disabled={gi === imgAttachments.length - 1}
                  title="Nächstes Bild"
                >›</button>
              )}
            </div>
            {imgAttachments.length > 1 && (
              <div className="recording-gallery-dots">
                {imgAttachments.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`recording-gallery-dot${i === gi ? ' is-active' : ''}`}
                    onClick={() => setGalleryIndex(i)}
                    aria-label={`Bild ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        <div className="recording-bar">
          <button className="recording-cancel" onClick={() => {
            // Mark as cancelled BEFORE stopping so rec.onstop skips the
            // transcribe pipeline. onstop also flips the state to idle,
            // so we don't do it here (would race the async stop).
            cancelledRef.current = true;
            stopRecording();
          }} title="Cancel">
            <X size={14} />
          </button>
          <div className="recording-indicator">
            {/* No separate "recording" dot — the live VU-equalizer below
                already signals "aufnahme läuft" unmistakably (Alex
                2026-06-20: the dot is redundant next to the waveform). */}
            {/* VU-equalizer: per-band frequency amplitudes from the live
                stream. Bars grow/shrink vertically as you speak — much
                more obviously "alive" than the prior threshold-row. */}
            <div className="recording-vu" aria-hidden>
              {audioBands.map((amp, i) => {
                // Floor at ~10% so the bar is always slightly visible
                // (= "yes, the meter is running") even on dead silence.
                const heightPct = Math.max(8, Math.round(amp * 100));
                const peak = amp >= 0.8;
                return (
                  <span
                    key={i}
                    className={`recording-vu-bar${peak ? ' is-peak' : ''}`}
                    style={{ height: `${heightPct}%` }}
                  />
                );
              })}
            </div>
            <span className="recording-time">{formatTime(micTime)}</span>
          </div>
          {/* Route toggle for a long-press (Prompt-Agent) recording — default
              Prompt-Agent; the message routes to the LOCKED target on stop, no
              review dialog (Alex, 2026-07-01). */}
          {voiceRouteActive && (
            <div className="voice-route" role="group" aria-label="Prompt-Agent-Aufnahme — Queue-Modifier">
              {/* Route is FIXED (Prompt-Agent); Queue is an on/off timing modifier:
                  ON = submit only once the agent is idle (Alex 2026-07-03). */}
              <span className="voice-route-fixed">Prompt-Agent</span>
              <button
                type="button"
                className={`voice-route-opt${voiceQueueOn ? ' is-active' : ''}`}
                onClick={() => setQueueOn(!voiceQueueOnRef.current)}
                title={voiceQueueOn ? 'Queue AN — wartet bis der Agent frei ist' : 'Queue AUS — geht sofort an den Prompt-Agent'}
              >
                Queue {voiceQueueOn ? 'an' : 'aus'}
              </button>
            </div>
          )}
          <button
            className={`recording-stop${voiceSendPressed ? ' is-pressed' : ''}`}
            disabled={voiceSendPressed}
            onClick={() => {
              // Immediate visual confirmation the tap landed — the
              // stop/transcribe pipeline is async so without this the
              // button looked unresponsive (Alex 2026-06-15).
              setVoiceSendPressed(true);
              if (navigator.vibrate) navigator.vibrate(30);
              stopRecording();
            }}
            title="Send Voice"
          >
            {voiceSendPressed ? <span className="cmd-spinner" /> : <ArrowUp size={16} />}
          </button>
        </div>
      </div>
    );
  }

  // ── Transcribing State ───────────────────────────────────────
  if (micState === 'transcribing' && isRecordingOwner) {
    return (
      <div className="input-bar-wrap">
        <div className="transcribing-bar">
          {/* Self-animating sound-wave (no live audio here) → conveys
              "audio is being converted" instead of a dead label (Alex
              2026-06-20). Bars flow in a staggered sine; the text carries
              a sweeping shimmer + breathing ellipsis. */}
          <div className="transcribing-wave" aria-hidden>
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <span
                key={i}
                className="transcribing-wave-bar"
                style={{ animationDelay: `${i * 0.1}s` }}
              />
            ))}
          </div>
          <span className="transcribing-text">
            Transkribiere<span className="transcribing-dots"><i>.</i><i>.</i><i>.</i></span>
          </span>
        </div>
      </div>
    );
  }

  // ── MCP Editor ───────────────────────────────────────────────
  if (showMcp) {
    return (
      <div className="input-bar-wrap">
        <div className="mcp-editor">
          <div className="mcp-editor-header">
            <span className="mcp-editor-title">MCP Server</span>
            <div className="mcp-editor-actions">
              <button className="rbac-btn" onClick={() => setShowMcp(false)}>Cancel</button>
              <button className="rbac-btn rbac-btn-ok" onClick={saveMcp}>Save</button>
            </div>
          </div>
          <div className="mcp-editor-grid">
            {ALL_MCP.map((s) => (
              <label key={s} className="mcp-editor-item">
                <input
                  type="checkbox"
                  checked={!!mcpServers[s]}
                  onChange={() => setMcpServers((prev) => ({ ...prev, [s]: !prev[s] }))}
                />
                <span className="mcp-editor-name">{s}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="input-bar-wrap">
      {/* Prompt-Agent intake result now renders in the shared top-right toast stack
          (GlobalToasts, #194) — no longer an inline bar here. */}
      {/* TTS Status + playback controls. Native/Siri only gets Stop
          (Web-Speech isn't seekable). OpenAI-wave gets 15s-rewind +
          pause/resume + Stop because it's a real HTMLAudioElement. */}
      {ttsStatus && (
        <div className="tts-status">
          <span className="tts-status-text">{ttsStatus}</span>
          {isSpeaking && (
            <span className="tts-controls">
              {ttsKind === 'openai' && (
                <>
                  <button
                    type="button"
                    className="tts-ctrl-btn"
                    onClick={() => rewindTTS(15)}
                    title="15 Sekunden zurück"
                  >
                    <RotateCcw size={13} /><span className="tts-ctrl-sec">15</span>
                  </button>
                  <button
                    type="button"
                    className="tts-ctrl-btn"
                    onClick={togglePauseTTS}
                    title={ttsPaused ? 'Weiter' : 'Pause'}
                  >
                    {ttsPaused ? <Play size={14} /> : <Pause size={14} />}
                  </button>
                </>
              )}
              <button
                type="button"
                className="tts-ctrl-btn tts-ctrl-stop"
                onClick={stopTTS}
                title="Stop"
              >
                <Square size={13} />
              </button>
            </span>
          )}
        </div>
      )}
      {uploadError && (
        <div className="upload-error" role="alert">
          <span>{uploadError}</span>
          <button type="button" onClick={() => setUploadError('')} aria-label="Uploadfehler schließen">
            <X size={12} />
          </button>
        </div>
      )}

      {/* Attachment Previews */}
      {attachments.length > 0 && (
        <div className="attachment-row">
          {attachments.map((att, i) => (
            <div key={i} className="attachment-item">
              {att.preview ? (
                <img src={att.preview} alt="" className="attachment-thumb" />
              ) : (
                <div className="attachment-file">{att.name.split('.').pop()}</div>
              )}
              <button className="attachment-remove" onClick={() => removeAttachment(i)}><X size={12} /></button>
            </div>
          ))}
          {uploading && (
            <div className="attachment-item attachment-loading">
              <div className="attachment-spinner" />
            </div>
          )}
        </div>
      )}
      {uploading && attachments.length === 0 && (
        <div className="attachment-row">
          <div className="attachment-item attachment-loading">
            <div className="attachment-spinner" />
          </div>
        </div>
      )}
      {/* Sichtbar machen, warum der Klick "nichts getan" hat — sonst wirkt das
          Warten wie ein hängender Knopf und der Nutzer drückt erneut. */}
      {pendingSend && uploading && (
        <div className="upload-pending-hint">
          <span className="cmd-spinner" />
          {pendingSend === 'queue'
            ? 'Wird eingereiht, sobald der Upload fertig ist'
            : pendingSend === 'promptAgent'
              ? 'Geht an den Prompt-Agent, sobald der Upload fertig ist'
              : 'Wird gesendet, sobald der Upload fertig ist'}
        </div>
      )}

      {queuedText && (
        <div className="queue-banner">
          <Clock size={12} className="queue-banner-icon" />
          {/* Ehrlich beschriftet (Alex 2026-08-06): die Queue liegt im
              Arbeitsspeicher DIESES Tabs — nicht im Backend. Reload oder
              Schließen verliert sie. Das alte „sendet sobald Agent grün"
              versprach Dauerhaftigkeit, die nicht dahintersteht; genau darauf
              hat sich Alex verlassen. Wird zurückgetauscht, sobald Clouds
              serverseitiges Zurückhalten steht. */}
          <span
            className="queue-banner-label"
            title="Diese Warteschlange lebt nur in diesem Browser-Tab. Neu laden oder schließen verwirft sie — der Server weiß nichts davon."
          >
            {agentBusy || optimisticBusy
              ? `Wartet in diesem Tab — sendet sobald Agent grün${
                (queuedList?.length || 0) > 1 ? ` · ${queuedList?.length} Nachrichten` : ''}`
              : 'Sending…'}
          </span>
          {/* Die Anzahl gehört sichtbar in die Leiste. Alex hat drei
              Nachrichten abgelegt und eine gesehen — verloren war keine, aber
              die Vorschau zeigte nur die ersten 80 Zeichen des ersten
              Eintrags, und damit sahen drei aus wie eine. Bei mehreren zeigt
              die Vorschau jetzt den JÜNGSTEN, weil das der ist, den man
              gerade abgelegt hat und wiedererkennen will. */}
          <span
            className="queue-banner-preview"
            title={(queuedList?.length || 0) > 1
              ? queuedList?.map((entry, i) => `${i + 1}. ${entry}`).join('\n\n')
              : undefined}
          >
            {queuedViaPa[sessionName] ? '→ Prompt-Agent · ' : ''}
            {(() => {
              const newest = queuedList?.length ? queuedList[queuedList.length - 1] : queuedText;
              return newest.length > 80 ? `${newest.slice(0, 80)}…` : newest;
            })()}
          </span>
          <button
            className="queue-banner-push"
            title="Jetzt senden — wartet nicht bis der Agent grün ist"
            aria-label="Wartende Nachricht jetzt senden"
            onClick={pushQueuedNow}
          >
            <ArrowUp size={12} />
            Push
          </button>
          <button
            className="queue-banner-cancel"
            title="Queue verwerfen"
            onClick={clearQueuedForSession}
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* Outbox / offline banner — only shown when at least one item
          has actually FAILED (attempts > 0). On the happy path the item
          is in the outbox for ~200 ms and then removed; we don't want
          that millisecond-window to flash a red banner. The banner only
          surfaces once the first POST attempt has thrown — that's the
          signal worth showing the user. */}
      {(() => {
        const failed = sessionOutbox.filter((x) => x.attempts > 0);
        if (failed.length === 0) return null;
        const latest = failed[failed.length - 1];
        return (
          <div className="outbox-banner">
            <Clock size={12} className="outbox-banner-icon" />
            <span className="outbox-banner-label">
              {!isOnline
                ? `Offline — ${failed.length} Nachricht${failed.length === 1 ? '' : 'en'} wartet`
                : `Retry läuft — ${failed.length} Nachricht${failed.length === 1 ? '' : 'en'} (Versuch ${Math.max(...failed.map((x) => x.attempts)) + 1})`}
            </span>
            <span className="outbox-banner-preview">
              {latest.text.length > 60
                ? latest.text.slice(0, 60) + '…'
                : latest.text}
            </span>
            <button
              className="outbox-banner-retry"
              title="Jetzt erneut versuchen"
              onClick={() => { failed.forEach((x) => void flushItem(x)); }}
            >
              ↻
            </button>
            <button
              className="outbox-banner-cancel"
              title="Alle wartenden Nachrichten dieser Session verwerfen"
              onClick={discardSession}
            >
              <X size={12} />
            </button>
          </div>
        );
      })()}

      {showKeys && (
        <div className="key-row">
          {KEY_BUTTONS.map((k) => (
            <button
              key={k.key}
              className={`key-btn ${k.cls}`}
              onClick={() => sendKey(k.key)}
            >
              {k.label}
            </button>
          ))}
        </div>
      )}

      <div className="input-bar">
        {/* Left slot. Default: "+" group-question (Alex 2026-06-20). With
            attachLeft (agent chat, "+" moved to the header): the media-attach
            paperclip lives here instead (Alex 2026-06-22). */}
        {attachLeft && storageAvailable ? (
          <button className="input-action" onClick={handleUpload} title="Medien/Dateien anhängen">
            <Paperclip size={16} />
          </button>
        ) : onAskGroup && (
          <button className="input-action" onClick={onAskGroup} title="Frage an mehrere Agenten (dieser ist vorgewählt)">
            <Plus size={16} />
          </button>
        )}

        <textarea
          ref={textareaRef}
          className="input-field"
          placeholder={placeholder ?? `Message ${sessionName}...`}
          value={input}
          rows={1}
          onChange={(e) => {
            setInput(e.target.value);
            // Grow to fit content in a coalesced rAF (see autoGrow) — NOT
            // inline, which forced a full reflow per character. CSS max-height
            // + overflow-y:auto handle the cap (50vh of the visual viewport).
            autoGrow();
          }}
          onKeyDown={handleKeyDown}
        />

        {/* Media attach (paperclip) after the textarea — unless it was moved
            to the left slot (attachLeft). */}
        {!attachLeft && storageAvailable && (
          <button className="input-action" onClick={handleUpload} title="Medien/Dateien anhängen">
            <Paperclip size={16} />
          </button>
        )}

        {aiAvailable && (
        <button
          className={`input-action mic-primary${!showSend ? ' mic-solo' : ''}${micState === 'error' ? ' error-state' : ''}`}
          // Disabled while a recording/transcribe is running on ANOTHER agent — one mic
          // at a time; prevents a tap here from stopping the origin agent's recording.
          disabled={(micState === 'recording' || micState === 'transcribing' || micState === 'review') && !isRecordingOwner}
          onClick={() => {
            if (micLongPressRef.current) { micLongPressRef.current = false; return; }
            handleMicClick();
          }}
          onTouchStart={(e) => {
            if (micState !== 'idle' && micState !== 'error') return;
            micLongPressRef.current = false;
            micTouchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            if (navigator.vibrate) navigator.vibrate(8); // immediate tick (#119)
            const btn = e.currentTarget;
            btn.classList.add('long-press-active');
            micTimerRef.current = setTimeout(() => {
              micLongPressRef.current = true;
              btn.classList.remove('long-press-active');
              if (navigator.vibrate) navigator.vibrate(50);
              // Long-press = a Prompt-Agent voice message. Lock the target agent NOW
              // so a tab-switch during transcription can't misroute it; default route
              // = Prompt-Agent, toggle shown next to the waveform (Alex, 2026-07-01).
              recordingTargetRef.current = sessionName;
              setQueueOn(false);
              voiceRouteActiveRef.current = true;
              setVoiceRouteActive(true);
              void startRecording();
            }, 500);
          }}
          onTouchEnd={(e) => { if (micTimerRef.current) clearTimeout(micTimerRef.current); e.currentTarget.classList.remove('long-press-active'); }}
          onTouchMove={(e) => {
            const s = micTouchStartRef.current;
            if (!s || !micTimerRef.current) return;
            if (Math.hypot(e.touches[0].clientX - s.x, e.touches[0].clientY - s.y) > 12) {
              clearTimeout(micTimerRef.current);
              micTimerRef.current = null;
              e.currentTarget.classList.remove('long-press-active');
            }
          }}
          onMouseDown={(e) => {
            if (micState !== 'idle' && micState !== 'error') return;
            micLongPressRef.current = false;
            const btn = e.currentTarget;
            btn.classList.add('long-press-active');
            micTimerRef.current = setTimeout(() => {
              micLongPressRef.current = true;
              btn.classList.remove('long-press-active');
              // Long-press = Prompt-Agent voice message (see touch handler).
              recordingTargetRef.current = sessionName;
              setQueueOn(false);
              voiceRouteActiveRef.current = true;
              setVoiceRouteActive(true);
              void startRecording();
            }, 500);
          }}
          onMouseUp={(e) => { if (micTimerRef.current) clearTimeout(micTimerRef.current); e.currentTarget.classList.remove('long-press-active'); }}
          onMouseLeave={(e) => { if (micTimerRef.current) clearTimeout(micTimerRef.current); e.currentTarget.classList.remove('long-press-active'); }}
          title="Tippen: aufnehmen + direkt an den Agenten. Lange drücken: Sprachnachricht für Prompt-Agent (Route währenddessen wählbar) — Ziel wird beim Aufnehmen gesperrt"
        >
          <Mic size={16} />
        </button>
        )}

        {!hideTts && (
        <div className="cmd-menu-anchor">
          <button
            className={`input-action${showMenu || showKeys ? ' active' : ''}${uploading ? ' uploading' : ''}${isSpeaking ? ' speaking' : ''}`}
            onClick={() => {
              if (!ttsLongPressRef.current) handleTTS();
              ttsLongPressRef.current = false;
            }}
            onTouchStart={(e) => {
              ttsLongPressRef.current = false;
              const btn = e.currentTarget;
              ttsTimerRef.current = setTimeout(() => {
                ttsLongPressRef.current = true;
                btn.classList.add('long-press-active');
                setShowMenu(!showMenu);
                if (navigator.vibrate) navigator.vibrate(50);
              }, 500);
            }}
            onTouchEnd={(e) => { if (ttsTimerRef.current) clearTimeout(ttsTimerRef.current); e.currentTarget.classList.remove('long-press-active'); }}
            onTouchMove={(e) => { if (ttsTimerRef.current) clearTimeout(ttsTimerRef.current); e.currentTarget.classList.remove('long-press-active'); }}
            onContextMenu={(e) => { e.preventDefault(); setShowMenu(!showMenu); }}
            title={isSpeaking ? 'Stop speaking' : 'TTS (long-press for menu)'}
          >
            {uploading ? <span className="cmd-spinner" /> : isSpeaking ? <Square size={16} /> : <Volume2 size={16} />}
          </button>

          {showMenu && createPortal(
            <>
              <div className="cmd-backdrop" onClick={() => setShowMenu(false)} />
              <div className="cmd-menu">
                <button className="cmd-item" onClick={handleURLs}>
                  <Link size={14} />
                  URLs extrahieren
                </button>
                <button className="cmd-item" onClick={openMcpEditor}>
                  <Settings size={14} />
                  MCP Server
                </button>
                <button
                  className="cmd-item"
                  onClick={() => {
                    setShowKeys(!showKeys);
                    setShowMenu(false);
                  }}
                >
                  <Keyboard size={14} />
                  {showKeys ? 'Keys ausblenden' : 'Keys einblenden'}
                </button>
                <button
                  className="cmd-item"
                  onClick={() => {
                    const next = !autoTts;
                    setAutoTts(next);
                    localStorage.setItem('cloud-auto-tts', next ? '1' : '0');
                    setShowMenu(false);
                  }}
                >
                  {autoTts ? <Check size={14} /> : <Circle size={14} />}
                  Auto-TTS {autoTts ? 'an' : 'aus'}
                </button>
                <button
                  className="cmd-item"
                  onClick={async () => {
                    const next = !notifyDone;
                    if (next) {
                      // Permission must be requested from this user
                      // gesture; only enable if actually granted.
                      const perm = await requestNotificationPermission();
                      if (perm !== 'granted') {
                        setTtsStatus('Benachrichtigungen vom Browser blockiert');
                        setTimeout(() => setTtsStatus(''), 3000);
                        setShowMenu(false);
                        return;
                      }
                    }
                    setNotifyDone(next);
                    setNotificationsEnabled(next);
                    setShowMenu(false);
                  }}
                  title="OS-Benachrichtigung + Tab-Badge wenn der Agent fertig ist (nur wenn Tab im Hintergrund)"
                >
                  {notifyDone ? <Check size={14} /> : <Circle size={14} />}
                  Fertig-Benachrichtigung {notifyDone ? 'an' : 'aus'}
                </button>
                <button
                  className="cmd-item"
                  onClick={() => {
                    // Cycle: native → openai-compact → openai-prose → native
                    const next =
                      ttsMode === 'native' ? 'openai-compact' :
                      ttsMode === 'openai-compact' ? 'openai-prose' :
                      'native';
                    setTtsMode(next);
                    localStorage.setItem('cloud-tts-mode', next);
                    setShowMenu(false);
                  }}
                  title="Click to cycle. Native = browser/Siri (free). OpenAI compact = 2-3 Sätze Driving-Mode. OpenAI prose = fließende Erzählung mit Details."
                >
                  {ttsMode === 'native' ? <Circle size={14} /> : <Check size={14} />}
                  TTS-Mode: {
                    ttsMode === 'native' ? 'Native (Siri/Browser)' :
                    ttsMode === 'openai-compact' ? 'OpenAI compact (Driving)' :
                    'OpenAI prose (Erzählung)'
                  }
                </button>
                <button
                  className="cmd-item"
                  onClick={() => {
                    setShowMenu(false);
                    handleOpenAITTS('compact');
                  }}
                  title="One-shot OpenAI TTS compact (Driving) — regardless of default mode"
                >
                  <Volume2 size={14} />
                  OpenAI TTS abspielen (compact)
                </button>
                <button
                  className="cmd-item"
                  onClick={() => {
                    setShowMenu(false);
                    handleOpenAITTS('prose');
                  }}
                  title="One-shot OpenAI TTS prose (Erzählung) — regardless of default mode"
                >
                  <Volume2 size={14} />
                  OpenAI TTS abspielen (prose)
                </button>
              </div>
            </>,
            document.body
          )}
        </div>
        )}

        {showSend && (
        <div className="send-menu-anchor">
          <button
            disabled={canSubmit === false}
            className={`input-action primary${queuedText ? ' has-queue' : ''}${showSendMenu ? ' active' : ''}${sentFlash ? ' is-sent' : ''}`}
            title={canSubmit === false
              ? 'Erst Empfänger wählen'
              : queuedText
                ? 'Queued — sendet wenn Agent grün. Lange drücken für Menü.'
                : 'Send · lange drücken für Queue-Optionen'}
            onClick={() => {
              // Long-press swallows the click — the timer already fired
              // and opened the menu. A plain short click sends now.
              if (sendLongPressRef.current) { sendLongPressRef.current = false; return; }
              send();
            }}
            onTouchStart={(e) => {
              sendLongPressRef.current = false;
              sendTouchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
              // Immediate light tick so the press registers in the hand; the ring
              // fills over 500 ms, then a stronger buzz confirms the menu (#119).
              if (navigator.vibrate) navigator.vibrate(8);
              const btn = e.currentTarget;
              // Immediate visual feedback so the user sees the press
              // *is* being detected — the menu only opens at 500 ms but
              // the pulse animation starts now.
              btn.classList.add('long-press-active');
              sendTimerRef.current = setTimeout(() => {
                sendLongPressRef.current = true;
                setShowSendMenu(true);
                if (navigator.vibrate) navigator.vibrate(50);
              }, 500);
            }}
            onTouchEnd={(e) => {
              if (sendTimerRef.current) clearTimeout(sendTimerRef.current);
              e.currentTarget.classList.remove('long-press-active');
            }}
            onTouchMove={(e) => {
              // Only cancel on REAL movement — iOS fires touchmove on sub-pixel
              // jitter, which was killing the press before the menu could open.
              const s = sendTouchStartRef.current;
              if (!s || !sendTimerRef.current) return;
              if (Math.hypot(e.touches[0].clientX - s.x, e.touches[0].clientY - s.y) > 12) {
                clearTimeout(sendTimerRef.current);
                sendTimerRef.current = null;
                e.currentTarget.classList.remove('long-press-active');
              }
            }}
            onMouseDown={(e) => {
              sendLongPressRef.current = false;
              const btn = e.currentTarget;
              btn.classList.add('long-press-active');
              sendTimerRef.current = setTimeout(() => {
                sendLongPressRef.current = true;
                setShowSendMenu(true);
              }, 500);
            }}
            onMouseUp={(e) => {
              if (sendTimerRef.current) clearTimeout(sendTimerRef.current);
              e.currentTarget.classList.remove('long-press-active');
            }}
            onMouseLeave={(e) => {
              if (sendTimerRef.current) clearTimeout(sendTimerRef.current);
              e.currentTarget.classList.remove('long-press-active');
            }}
            onContextMenu={(e) => { e.preventDefault(); setShowSendMenu(true); }}
          >
            {sentFlash ? <Check size={16} /> : queuedText ? <Clock size={16} /> : <ArrowUp size={16} />}
          </button>

          {showSendMenu && createPortal(
            <>
              <div className="cmd-backdrop" onClick={() => setShowSendMenu(false)} />
              <div className="cmd-menu cmd-menu-right">
                <button
                  className="cmd-item"
                  onClick={() => {
                    queueSend();
                    setShowSendMenu(false);
                  }}
                  disabled={!input.trim() && attachments.length === 0}
                  title="Sendet die Nachricht erst wenn der Agent grün ist — du belastest ihn nicht während er arbeitet"
                >
                  <Clock size={14} />
                  In Queue legen (sendet wenn Agent grün)
                </button>
                <button
                  className="cmd-item"
                  onClick={() => {
                    send();
                    setShowSendMenu(false);
                  }}
                  disabled={!input.trim() && attachments.length === 0}
                >
                  <ArrowUp size={14} />
                  Sofort senden
                </button>
                <button
                  className="cmd-item"
                  onClick={() => {
                    promptAgentSend();
                    setShowSendMenu(false);
                  }}
                  disabled={!input.trim() && attachments.length === 0}
                  title="Schickt den Prompt erst an den Prompt-Agent — der veredelt + klassifiziert ihn und leitet ihn strukturiert an den Agenten weiter"
                >
                  <Wand2 size={14} />
                  Über Prompt Agent
                </button>
                {/* Issue #943: dasselbe wie darüber, nur reiht das entstehende
                    Issue VOR die wartenden statt hinten anzustellen. Zwei
                    Einträge statt eines Schalters — so ist beim Klick sichtbar,
                    was passiert, und es braucht keinen gemerkten Zustand. */}
                <button
                  className="cmd-item"
                  onClick={() => {
                    promptAgentSend('front');
                    setShowSendMenu(false);
                  }}
                  disabled={!input.trim() && attachments.length === 0}
                  title="Wie oben, aber das Issue wird ganz vorne eingereiht — der Agent nimmt es als Nächstes"
                >
                  <Wand2 size={14} />
                  Prompt Agent · als Nächstes
                </button>
                {queuedText && (
                  <button
                    className="cmd-item"
                    onClick={() => {
                      clearQueuedForSession();
                      setShowSendMenu(false);
                    }}
                  >
                    <X size={14} />
                    Queue verwerfen
                  </button>
                )}
              </div>
            </>,
            document.body
          )}
        </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div>
  );
});
